/**
 * AI Writing Studio — APX WorkCenter
 * Two-panel layout: template picker + prompt on the left, generated content on the right.
 * Saved documents listed below.
 */

import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Streamdown } from "streamdown";
import { toast } from "sonner";
import {
  FileText,
  Wand2,
  Save,
  Copy,
  Trash2,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Edit3,
  X,
  Check,
  Loader2,
  PenLine,
} from "lucide-react";

// ─── Template Definitions ─────────────────────────────────────────────────────

const TEMPLATE_CATEGORIES = [
  {
    label: "Client Letters",
    value: "client_letter",
    icon: "📄",
    description: "Professional letters to clients",
    defaultPrompt:
      "Write a professional letter to the client regarding their account status. Include a greeting, main message, and call to action.",
  },
  {
    label: "Engagement Letters",
    value: "engagement_letter",
    icon: "🤝",
    description: "Service engagement agreements",
    defaultPrompt:
      "Draft an engagement letter outlining the scope of bookkeeping/CFO services, fees, and terms for a new client.",
  },
  {
    label: "Collection Notices",
    value: "collection_notice",
    icon: "⚠️",
    description: "Past-due payment notices",
    defaultPrompt:
      "Write a firm but professional collection notice for an overdue invoice. Include the amount due, due date, and payment instructions.",
  },
  {
    label: "Tax Notice Responses",
    value: "tax_notice_response",
    icon: "🏛️",
    description: "IRS / state agency responses",
    defaultPrompt:
      "Draft a response to an IRS or state tax agency notice. Address the notice professionally, provide clarification, and request resolution.",
  },
  {
    label: "Contracts",
    value: "contract",
    icon: "📋",
    description: "Service contracts & agreements",
    defaultPrompt:
      "Create a service contract for ongoing fractional CFO or bookkeeping services. Include scope, payment terms, termination clause, and confidentiality.",
  },
  {
    label: "Estimates",
    value: "estimate",
    icon: "💰",
    description: "Project / service estimates",
    defaultPrompt:
      "Write a professional estimate for bookkeeping or CFO advisory services. Include line items, pricing, and validity period.",
  },
  {
    label: "Industry KPI Reports",
    value: "kpi_report",
    icon: "📊",
    description: "Industry-specific KPI analysis",
    defaultPrompt:
      "Generate an industry KPI report for a restaurant/automotive/retail client. Include relevant benchmarks, current performance summary, and recommendations.",
  },
  {
    label: "General / Free-form",
    value: "freeform",
    icon: "✏️",
    description: "Any business writing",
    defaultPrompt: "",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function templateLabel(value: string) {
  return TEMPLATE_CATEGORIES.find((t) => t.value === value)?.label ?? value;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AIWritingStudio() {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // ── Left panel state ──
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_CATEGORIES[0]);
  const [prompt, setPrompt] = useState(TEMPLATE_CATEGORIES[0].defaultPrompt);
  const [clientName, setClientName] = useState("");

  // ── Right panel state ──
  const [generatedContent, setGeneratedContent] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState("");

  // ── Saved docs state ──
  const [expandedDocId, setExpandedDocId] = useState<number | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");

  const contentRef = useRef<HTMLTextAreaElement>(null);

  // ── tRPC ──
  const generateMutation = trpc.aiWriting.generate.useMutation({
    onSuccess: (data) => {
      const content = typeof data.content === "string" ? data.content : String(data.content ?? "");
      setGeneratedContent(content);
      setEditableContent(content);
      setIsEditing(false);
      // Auto-generate a title from the first line
      const firstLine = content.split("\n")[0].replace(/^#+\s*/, "").trim();
      if (!docTitle) {
        setDocTitle(
          firstLine.length > 60
            ? firstLine.slice(0, 60) + "…"
            : firstLine || `${selectedTemplate.label} — ${new Date().toLocaleDateString()}`
        );
      }
    },
    onError: (err) => {
      toast.error("Generation failed: " + err.message);
    },
  });

  const saveMutation = trpc.aiWriting.save.useMutation({
    onSuccess: () => {
      toast.success("Document saved.");
      utils.aiWriting.list.invalidate();
    },
    onError: (err) => toast.error("Save failed: " + err.message),
  });

  const updateMutation = trpc.aiWriting.update.useMutation({
    onSuccess: () => {
      toast.success("Document updated.");
      utils.aiWriting.list.invalidate();
    },
    onError: (err) => toast.error("Update failed: " + err.message),
  });

  const deleteMutation = trpc.aiWriting.delete.useMutation({
    onSuccess: () => {
      toast.success("Document deleted.");
      utils.aiWriting.list.invalidate();
    },
    onError: (err) => toast.error("Delete failed: " + err.message),
  });

  const { data: savedDocs = [], isLoading: docsLoading } = trpc.aiWriting.list.useQuery(
    { ownerEmail: user?.email ?? "" },
    { enabled: !!user?.email }
  );

  // ── Handlers ──

  function handleTemplateSelect(tpl: (typeof TEMPLATE_CATEGORIES)[0]) {
    setSelectedTemplate(tpl);
    setPrompt(tpl.defaultPrompt);
  }

  function handleGenerate() {
    if (!prompt.trim()) {
      toast.error("Please enter instructions before generating.");
      return;
    }
    setGeneratedContent("");
    setDocTitle("");
    generateMutation.mutate({
      templateType: selectedTemplate.value,
      prompt: prompt.trim(),
      clientName: clientName.trim() || undefined,
    });
  }

  function handleSave() {
    if (!generatedContent) {
      toast.error("Nothing to save — generate a document first.");
      return;
    }
    if (!docTitle.trim()) {
      toast.error("Please enter a title before saving.");
      return;
    }
    saveMutation.mutate({
      ownerEmail: user!.email,
      ownerName: user!.name,
      title: docTitle.trim(),
      templateType: selectedTemplate.value,
      prompt: prompt.trim(),
      content: isEditing ? editableContent : generatedContent,
      clientName: clientName.trim() || undefined,
    });
  }

  function handleCopy() {
    const text = isEditing ? editableContent : generatedContent;
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard."));
  }

  function handleEditToggle() {
    if (!isEditing) {
      setEditableContent(generatedContent);
      setIsEditing(true);
    } else {
      setGeneratedContent(editableContent);
      setIsEditing(false);
    }
  }

  function handleOpenDoc(doc: (typeof savedDocs)[0]) {
    setSelectedTemplate(
      TEMPLATE_CATEGORIES.find((t) => t.value === doc.templateType) ?? TEMPLATE_CATEGORIES[7]
    );
    setPrompt(doc.prompt);
    setClientName(doc.clientName ?? "");
    setGeneratedContent(doc.content);
    setEditableContent(doc.content);
    setDocTitle(doc.title);
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleDeleteDoc(id: number) {
    if (!user?.email) return;
    deleteMutation.mutate({ id, ownerEmail: user.email });
    if (expandedDocId === id) setExpandedDocId(null);
  }

  function handleSaveTitleEdit(doc: (typeof savedDocs)[0]) {
    if (!user?.email || !editingTitleValue.trim()) return;
    updateMutation.mutate({ id: doc.id, ownerEmail: user.email, title: editingTitleValue.trim() });
    setEditingTitleId(null);
  }

  const isGenerating = generateMutation.isPending;
  const isSaving = saveMutation.isPending;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[oklch(0.72_0.14_75/15%)] flex items-center justify-center">
            <PenLine className="w-5 h-5 text-[oklch(0.72_0.14_75)]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">AI Writing Studio</h1>
            <p className="text-sm text-muted-foreground">
              Generate professional business documents powered by AI
            </p>
          </div>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
          {/* ── Left Panel: Template + Prompt ── */}
          <div className="space-y-4">
            {/* Template Picker */}
            <div className="rounded-xl border border-border bg-card p-4">
              <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[oklch(0.72_0.14_75)]" />
                Document Type
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATE_CATEGORIES.map((tpl) => (
                  <button
                    key={tpl.value}
                    onClick={() => handleTemplateSelect(tpl)}
                    className={`text-left p-2.5 rounded-lg border text-xs transition-all ${
                      selectedTemplate.value === tpl.value
                        ? "border-[oklch(0.72_0.14_75)] bg-[oklch(0.72_0.14_75/10%)] text-foreground"
                        : "border-border bg-background text-muted-foreground hover:border-[oklch(0.72_0.14_75/50%)] hover:text-foreground"
                    }`}
                  >
                    <span className="text-base mr-1">{tpl.icon}</span>
                    <span className="font-medium">{tpl.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Client Name */}
            <div className="rounded-xl border border-border bg-card p-4">
              <label className="text-sm font-semibold text-foreground mb-2 block">
                Client Name <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. Skyline Builders, APX Advisors"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[oklch(0.72_0.14_75/50%)]"
              />
            </div>

            {/* Prompt */}
            <div className="rounded-xl border border-border bg-card p-4">
              <label className="text-sm font-semibold text-foreground mb-2 block">
                Instructions
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={8}
                placeholder="Describe what you want the document to say, include any specific details, names, amounts, dates, or tone requirements…"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[oklch(0.72_0.14_75/50%)] resize-none"
              />
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-[oklch(0.72_0.14_75)] hover:bg-[oklch(0.65_0.14_75)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 text-sm transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    Generate Document
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Right Panel: Generated Content ── */}
          <div className="rounded-xl border border-border bg-card flex flex-col min-h-[500px]">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex-1 mr-3">
                <input
                  type="text"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  placeholder="Document title…"
                  className="w-full bg-transparent text-sm font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                {generatedContent && (
                  <>
                    <button
                      onClick={handleEditToggle}
                      title={isEditing ? "Preview" : "Edit"}
                      className={`p-1.5 rounded-lg transition-colors text-xs flex items-center gap-1 ${
                        isEditing
                          ? "bg-[oklch(0.72_0.14_75/15%)] text-[oklch(0.72_0.14_75)]"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      {isEditing ? "Preview" : "Edit"}
                    </button>
                    <button
                      onClick={handleCopy}
                      title="Copy to clipboard"
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-xs flex items-center gap-1"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={isSaving}
                      title="Save document"
                      className="p-1.5 rounded-lg bg-[oklch(0.72_0.14_75)] hover:bg-[oklch(0.65_0.14_75)] text-white disabled:opacity-50 transition-colors text-xs flex items-center gap-1"
                    >
                      {isSaving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      Save
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 p-5 overflow-auto">
              {isGenerating && !generatedContent && (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-[oklch(0.72_0.14_75)]" />
                  <p className="text-sm">Generating your document…</p>
                </div>
              )}

              {!isGenerating && !generatedContent && (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
                  <Wand2 className="w-10 h-10 opacity-20" />
                  <p className="text-sm text-center max-w-xs">
                    Select a template, enter your instructions, and click{" "}
                    <strong>Generate Document</strong> to get started.
                  </p>
                </div>
              )}

              {generatedContent && !isEditing && (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <Streamdown>{generatedContent}</Streamdown>
                </div>
              )}

              {generatedContent && isEditing && (
                <textarea
                  ref={contentRef}
                  value={editableContent}
                  onChange={(e) => setEditableContent(e.target.value)}
                  className="w-full h-full min-h-[400px] bg-transparent text-sm text-foreground font-mono focus:outline-none resize-none"
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Saved Documents ── */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <FolderOpen className="w-4 h-4 text-[oklch(0.72_0.14_75)]" />
            <h2 className="text-base font-semibold text-foreground">Saved Documents</h2>
            {savedDocs.length > 0 && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {savedDocs.length}
              </span>
            )}
          </div>

          {docsLoading && (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading saved documents…
            </div>
          )}

          {!docsLoading && savedDocs.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground text-sm">
              No saved documents yet. Generate and save a document to see it here.
            </div>
          )}

          {!docsLoading && savedDocs.length > 0 && (
            <div className="space-y-2">
              {[...savedDocs].reverse().map((doc) => (
                <div
                  key={doc.id}
                  className="rounded-xl border border-border bg-card overflow-hidden"
                >
                  {/* Doc row */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="text-lg">
                      {TEMPLATE_CATEGORIES.find((t) => t.value === doc.templateType)?.icon ?? "📄"}
                    </span>

                    {/* Title (inline edit) */}
                    <div className="flex-1 min-w-0">
                      {editingTitleId === doc.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveTitleEdit(doc);
                              if (e.key === "Escape") setEditingTitleId(null);
                            }}
                            autoFocus
                            className="flex-1 bg-background border border-border rounded px-2 py-1 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[oklch(0.72_0.14_75)]"
                          />
                          <button
                            onClick={() => handleSaveTitleEdit(doc)}
                            className="text-green-500 hover:text-green-400"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingTitleId(null)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">
                            {doc.title}
                          </span>
                          <button
                            onClick={() => {
                              setEditingTitleId(doc.id);
                              setEditingTitleValue(doc.title);
                            }}
                            className="text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {templateLabel(doc.templateType)}
                        </span>
                        {doc.clientName && (
                          <>
                            <span className="text-xs text-muted-foreground">·</span>
                            <span className="text-xs text-muted-foreground">{doc.clientName}</span>
                          </>
                        )}
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(doc.updatedAt)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenDoc(doc)}
                        title="Open in editor"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors text-xs flex items-center gap-1"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        Open
                      </button>
                      <button
                        onClick={() =>
                          setExpandedDocId(expandedDocId === doc.id ? null : doc.id)
                        }
                        title="Preview"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        {expandedDocId === doc.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteDoc(doc.id)}
                        title="Delete"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded preview */}
                  {expandedDocId === doc.id && (
                    <div className="border-t border-border px-5 py-4 bg-muted/30">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        <Streamdown>{doc.content}</Streamdown>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
